import { ArrowUpRight, Calendar, Play } from 'lucide-react'
import { Link } from 'react-router-dom'
import { MediaThumbnail } from '@/components/media/MediaThumbnail'
import { MediaVideoBadges } from '@/components/media/MediaVideoBadges'
import { formatMediaDate } from '@/lib/mediaUtils'
import { trackMediaEvent } from '@/lib/analytics/mediaEvents'
import { PATHS } from '@/routes/paths'
import type { MediaVideo } from '@/types/media'
import { cn } from '@/lib/utils'

interface MediaVideoCardProps {
  video: MediaVideo
  onPlay?: (video: MediaVideo) => void
  className?: string
  compact?: boolean
}

export function MediaVideoCard({ video, onPlay, className, compact }: MediaVideoCardProps) {
  const published = formatMediaDate(video.publishedAt)

  const handlePlay = () => {
    trackMediaEvent('media_video_click', {
      video_id: video.id,
      video_slug: video.slug,
      platform: video.platform,
      category: video.category?.slug,
    })
    onPlay?.(video)
  }

  return (
    <article
      className={cn(
        'group flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-100 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)] transition-shadow hover:shadow-[0_4px_20px_rgba(15,93,154,0.1)]',
        className,
      )}
    >
      <button
        type="button"
        onClick={handlePlay}
        className="relative block w-full overflow-hidden text-left"
        aria-label={`Play ${video.title}`}
      >
        <div
          className={cn(
            'relative overflow-hidden bg-neutral-100',
            // Homepage / listing cards use YouTube 16:9; compact keeps reel portrait.
            compact ? 'aspect-[9/14]' : 'aspect-video',
          )}
        >
          <MediaThumbnail
            src={video.thumbnail}
            alt={video.title}
            platform={video.platform}
            socialUrl={video.socialUrl}
            videoId={video.id}
            className="transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-linear-to-t from-black/50 via-transparent to-black/10" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-primary-700 shadow-sm backdrop-blur-sm">
              <Play className="ml-0.5 h-4 w-4 fill-current" />
            </span>
          </div>
          <MediaVideoBadges
            platform={video.platform}
            category={video.category?.name}
            className="absolute left-2.5 top-2.5"
          />
        </div>
      </button>

      <div className={cn('flex flex-1 flex-col', compact ? 'px-2.5 py-2' : 'px-3 py-2.5')}>
        <h3 className="font-display text-sm font-bold leading-snug text-neutral-900 line-clamp-2 group-hover:text-primary-600">
          {video.title}
        </h3>
        {!compact && video.shortDescription ? (
          <p className="mt-1 text-[11px] leading-relaxed text-neutral-500 line-clamp-2">
            {video.shortDescription}
          </p>
        ) : null}
        <div className="mt-2 flex items-center justify-between gap-2">
          {published ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-neutral-400">
              <Calendar className="h-3 w-3 shrink-0" />
              {published}
            </span>
          ) : (
            <span />
          )}
          <Link
            to={PATHS.mediaDetail(video.slug)}
            className="inline-flex shrink-0 items-center gap-0.5 text-[11px] font-semibold text-primary-600 transition-colors group-hover:text-primary-700"
            onClick={() =>
              trackMediaEvent('media_video_click', {
                video_id: video.id,
                video_slug: video.slug,
                source: 'details_button',
              })
            }
          >
            Details
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </article>
  )
}
